import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, Input, PLATFORM_ID, ViewChild, ElementRef, AfterViewInit } from '@angular/core';

@Component({
  selector: 'app-hero-section',
  imports: [CommonModule],
  templateUrl: './hero-section.html',
  styleUrl: './hero-section.scss'
})
export class HeroSection {

  @Input() text: string = '';
  @Input() image: string = '';
  @Input() title: string = '';
  /** Optional two-tone text heading (HTML, e.g. '<span class="gradient-text">app</span> development').
     When set, renders as a styled heading instead of the SVG `title` image. */
  @Input() titleHtml: string = '';
  @Input() height: string = '525px';
  @Input() opacity: string = '0.1';
  @Input() paddingTop: string = '420px';
  @Input() backgroundImage: string = ''
  /** Optional looping muted background video (mp4). When set, replaces the image background. */
  @Input() videoSrc: string = '';
  /** Optional poster for the video; falls back to `backgroundImage`. */
  @Input() videoPoster: string = '';
  /** Content alignment: 'center' (default) or 'left' (editorial split — text column over full-bleed video). */
  @Input() align: 'center' | 'left' = 'center';
  /** Optional hero subcopy paragraph (used by the left/editorial layout). */
  @Input() subcopy: string = '';
  @Input() projectName: string = '';
  @Input() responsive: boolean = false;

  @ViewChild('heroVideo') private heroVideo?: ElementRef<HTMLVideoElement>;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

  ngOnInit() {
    this.preloadImage();
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    const v = this.heroVideo?.nativeElement;
    if (!v) return;
    // Force the muted *property* (the attribute alone can be ignored under Angular) so
    // browser autoplay policies permit playback, then start it — with a retry once the
    // media is buffered (covers Safari/iOS timing).
    v.muted = true;
    const tryPlay = () => v.play().catch(() => { /* some browsers may still block; poster remains */ });
    tryPlay();
    v.addEventListener('loadeddata', tryPlay, { once: true });
  }

  ngOnChanges() {
    this.preloadImage();
  }

  get gradient() {
    return `linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.5) 34%,
      rgba(0, 0, 0, 0.9) 70.5%,
      #000000 100%
    )`;
  }

  private preloadImage() {
    if (isPlatformBrowser(this.platformId) && this.backgroundImage) {
      const preload = new Image();
      preload.src = this.backgroundImage;
      (preload as any).fetchPriority = 'high';
    }
  }

  get style() {
    const isUrl = this.backgroundImage.startsWith('http://') || this.backgroundImage.startsWith('https://');

    const gradient = `linear-gradient(
    180deg, 
    rgba(0, 0, 0, 0) 0%, 
    rgba(0, 0, 0, 0.5) 34%, 
    rgba(0, 0, 0, 0.9) 70.5%, 
    #000000 100%
  )`;

    return {
      'background-image': isUrl
        ? `${gradient}, url('${this.backgroundImage}')`
        : `url('${this.backgroundImage}')`,
      'height': this.height,
      'opacity': this.opacity,
      'background-size': 'cover',
      'background-position': 'center',
      'background-repeat': 'no-repeat',
    };
  }

}
